import type { Router } from 'vue-router'

import PAGE_NAME from '@web/consts/PAGE_NAME'
import { readOnboardingProgress, writeOnboardingProgress } from './progress'
import type { OnboardingContext, OnboardingDestination } from './types'

const SCENARIO_INF_SP = 'inf_sp'
const SCENARIO_ACCOUNT_SETUP = 'account-setup'

// Duplicated from registry to avoid circular import resolver <-> registry.
// Source of truth: registry.ts scenarios[slug].steps.length
// Must be updated if scenario step counts change.
const KNOWN_SCENARIOS_MAX_STEPS: Record<string, number> = {
  [SCENARIO_INF_SP]: 4,
  [SCENARIO_ACCOUNT_SETUP]: 3
}

// Duplicated from registry to avoid circular import resolver <-> registry.
// Must be updated when routeName of any step changes.
const KNOWN_STEP_ROUTE_NAMES: Record<string, Partial<Record<number, string>>> = {
  [SCENARIO_ACCOUNT_SETUP]: {
    1: PAGE_NAME.PHONE_VERIFICATION,
    2: PAGE_NAME.UPDATE_PROFILE,
    3: PAGE_NAME.LINK_SOCIAL_NETWORKS
  }
}

export const ONBOARDING_STEP_ROUTE_NAMES: ReadonlySet<string> = new Set(
  Object.values(KNOWN_STEP_ROUTE_NAMES)
    .flatMap(steps => Object.values(steps))
    .filter((name): name is string => typeof name === 'string')
)

function getStepRouteName (slug: string, step: number): string | undefined {
  return KNOWN_STEP_ROUTE_NAMES[slug]?.[step]
}

function makeOnboardingDest (slug: string, step: number): OnboardingDestination {
  return { kind: 'onboarding', slug, step, routeName: getStepRouteName(slug, step) }
}

export function resolveOnboardingDestination (ctx: OnboardingContext): OnboardingDestination {
  const progress = readOnboardingProgress()
  if (progress) {
    const max = KNOWN_SCENARIOS_MAX_STEPS[progress.slug]
    if (max && progress.step >= 1 && progress.step <= max) {
      return makeOnboardingDest(progress.slug, progress.step)
    }
  }

  if (ctx.user.requirePhoneValidate) {
    return makeOnboardingDest(SCENARIO_ACCOUNT_SETUP, 1)
  }

  if (ctx.user.requiredUpdateProfile) {
    return makeOnboardingDest(SCENARIO_ACCOUNT_SETUP, 2)
  }

  if (ctx.isTake) {
    return { kind: 'home' }
  }

  if (ctx.isShowNewLk && ctx.hasSuperSubscription) {
    return makeOnboardingDest(SCENARIO_INF_SP, 1)
  }

  return { kind: 'home' }
}

export function resolveAfterScenarioComplete (
  slug: string,
  ctx: OnboardingContext
): OnboardingDestination {
  if (slug === SCENARIO_ACCOUNT_SETUP) {
    if (ctx.isShowNewLk && ctx.hasSuperSubscription) {
      return makeOnboardingDest(SCENARIO_INF_SP, 1)
    }
    return { kind: 'home' }
  }
  return { kind: 'home' }
}

export function shouldCompleteOnboarding (ctx: OnboardingContext): boolean {
  if (ctx.user.requirePhoneValidate) return true
  if (ctx.user.requiredUpdateProfile) return true
  return false
}

export function applyDestination (router: Router, dest: OnboardingDestination): void {
  switch (dest.kind) {
    case 'onboarding':
      if (dest.routeName) {
        writeOnboardingProgress({ slug: dest.slug, step: dest.step })
        router.push({ name: dest.routeName })
      } else {
        router.push({
          name: PAGE_NAME.ONBOARDING,
          params: { name: dest.slug, step: String(dest.step) }
        })
      }
      return
    case 'home':
      router.push({ name: PAGE_NAME.HOME })
      return
    case 'returnUrl':
      router.push(dest.url)
      return
    case 'external':
      window.location.href = dest.url
  }
}
